import { useEffect, useState } from 'react'
import { Form, Input, Switch, Button, Space, Card, Row, Col, Result } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../hooks'
import { showMessageSuccess } from '../../helpers/functions'
import useModal from 'src/helpers/modal/useModal'

const formItemLayout = {
   labelCol: {
      xs: { span: 24 },
      sm: { span: 6 }
   },
   wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
   }
}

const WebhookManagement = observer(() => {
   const WebhookConfigStore = useStore('WebhookConfigStore')
   const AuthStore = useStore('AuthStore')
   const { webhookConfig } = WebhookConfigStore
   const [form] = Form.useForm()
   const { confirm } = useModal()

   const isEnabled = !!AuthStore.data_getUserInfo?.webhookEnabled

   const [isActive, setIsActive] = useState(true)
   const [autoPayment, setAutoPayment] = useState(false)
   const [loading, setLoading] = useState(false)
   const [testing, setTesting] = useState(false)

   useEffect(() => {
      if (isEnabled) {
         getConfig()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isEnabled])

   useEffect(() => {
      if (webhookConfig) {
         form.setFieldsValue({
            url: webhookConfig.url,
            secret: webhookConfig.secret || ''
         })
         setIsActive(webhookConfig.isActive !== false)
         setAutoPayment(webhookConfig.autoPayment === true)
      } else {
         form.resetFields()
         setIsActive(true)
         setAutoPayment(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [webhookConfig])

   const getConfig = async () => {
      await WebhookConfigStore.getConfig()
   }

   const handleSave = async (values: any) => {
      try {
         setLoading(true)
         const result = await WebhookConfigStore.upsertConfig({
            url: values.url?.trim() || '',
            secret: values.secret?.trim() || null,
            isActive,
            autoPayment
         })
         if (result) {
            showMessageSuccess('Cập nhật webhook thành công')
         }
      } finally {
         setLoading(false)
      }
   }

   const handleTest = async () => {
      try {
         setTesting(true)
         const result = await WebhookConfigStore.testConfig()
         if (result) {
            showMessageSuccess('Đã gửi webhook thử nghiệm')
         }
      } finally {
         setTesting(false)
      }
   }

   const handleDelete = () => {
      confirm({
         title: 'Bạn muốn xoá cấu hình webhook này?',
         icon: <ExclamationCircleOutlined />,
         okText: 'Đồng ý',
         cancelText: 'Huỷ',
         onCancel() {},
         async onOk() {
            const result = await WebhookConfigStore.deleteConfig()
            if (result) {
               showMessageSuccess('Đã xoá cấu hình webhook')
               form.resetFields()
               setIsActive(true)
               setAutoPayment(false)
            }
         }
      })
   }

   const hasConfig = !!webhookConfig?.id

   if (!isEnabled) {
      return (
         <Result
            status="403"
            title="Chưa được cấp quyền"
            subTitle="Tài khoản của bạn chưa được mở quyền cấu hình webhook. Vui lòng liên hệ quản trị viên."
         />
      )
   }

   return (
      <Row>
         <Col xs={24} md={16} lg={14}>
            <Card title="Cấu hình Webhook" bordered>
               <Form {...formItemLayout} form={form} name="WebhookConfig" onFinish={handleSave} scrollToFirstError>
                  <Form.Item
                     name="url"
                     label="URL Webhook"
                     extra="Có thể để trống nếu chỉ sử dụng tự động thanh toán."
                     rules={[{ type: 'url', message: 'URL không hợp lệ' }]}
                  >
                     <Input placeholder="https://your-domain.com/webhook" maxLength={1000} allowClear />
                  </Form.Item>

                  <Form.Item
                     name="secret"
                     label="Secret"
                     extra="Dùng để ký HMAC-SHA256 cho payload (header X-Webhook-Signature). Có thể để trống."
                  >
                     <Input.Password placeholder="Secret (tuỳ chọn)" maxLength={255} allowClear />
                  </Form.Item>

                  <Form.Item label="Kích hoạt">
                     <Switch
                        checked={isActive}
                        onChange={setIsActive}
                        checkedChildren="Bật"
                        unCheckedChildren="Tắt"
                     />
                  </Form.Item>

                  <Form.Item
                     label="Tự động thanh toán"
                     extra="Tự động xác nhận đơn và trừ tiền ngay sau khi tạo qua API v2."
                  >
                     <Switch
                        checked={autoPayment}
                        onChange={setAutoPayment}
                        checkedChildren="Bật"
                        unCheckedChildren="Tắt"
                     />
                  </Form.Item>

                  <div className="text-right">
                     <Space>
                        {hasConfig && (
                           <Button danger onClick={handleDelete}>
                              Xoá cấu hình
                           </Button>
                        )}
                        {hasConfig && (
                           <Button onClick={handleTest} loading={testing} disabled={!webhookConfig?.url}>
                              Gửi thử nghiệm
                           </Button>
                        )}
                        <Button type="primary" htmlType="submit" className="btn-add" loading={loading}>
                           Lưu
                        </Button>
                     </Space>
                  </div>
               </Form>
            </Card>
         </Col>
      </Row>
   )
})

export default WebhookManagement
